import type { CSSProperties, ReactElement, ReactNode } from 'react';

import type { VariantProps } from 'class-variance-authority';
import { ChevronDown } from 'lucide-react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore posthog is in the parent dir
import { usePostHog } from 'posthog-js/react';

import { Button, buttonVariants } from 'components/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/Dropdown';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Typography } from 'components/typography';
import { Maybe } from 'types/UtilityTypes';
import { cn } from 'utils/shadcn';

export type SplitButtonMenuItem = {
  id: string;
  label: ReactNode;
  description?: ReactNode;
  ariaLabel?: string;
  trailing?: ReactNode;
  icon?: ReactNode;
  buttonNamePosthog?: Maybe<string>;
  onSelect: () => void;
};

type SplitButtonVariant = 'default' | 'secondary';

type SplitButtonSize = VariantProps<typeof buttonVariants>['size'];

function renderMenuItemDescription(description: ReactNode) {
  if (!description) {
    return null;
  }

  if (typeof description === 'string') {
    return (
      <Typography
        variant='body-xs'
        textColor='muted'
        as='div'
        className='mt-0.5 leading-4'
      >
        {description}
      </Typography>
    );
  }

  return description;
}

function renderMenuItemLabel(
  label: ReactNode,
  variant: 'button' | 'body-sm',
  as: 'span' | 'div'
) {
  if (typeof label !== 'string') {
    return label;
  }

  return (
    <Typography variant={variant} weight='medium' as={as}>
      {label}
    </Typography>
  );
}

type SplitButtonProps = {
  primaryLabel: ReactNode;
  primaryAriaLabel?: string;
  primaryBusy?: boolean;
  primaryTestId?: string;
  primaryTooltip?: string;
  segmentStyle?: CSSProperties;
  menuContentStyle?: CSSProperties;
  primaryButtonNamePosthog: Maybe<string>;
  onPrimaryClick: () => void;
  menuItems?: SplitButtonMenuItem[];
  menuTriggerAriaLabel: string;
  variant?: SplitButtonVariant;
  size?: SplitButtonSize;
  // Height/typography for both segments; horizontal padding is primary-only.
  segmentClassName?: string;
  className?: string;
  primaryClassName?: string;
  menuTriggerClassName?: string;
  disabled?: boolean;
  // Forwarded to primary Button capture and menu item captures.
  logProperties?: Record<string, unknown>;
};

function SplitButtonTooltip({
  text,
  children,
}: {
  text?: string;
  children: ReactElement;
}) {
  if (!text) return children;
  return (
    <Tooltip delayDuration={500}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className='max-w-72 text-xs leading-4'>
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

// Primary action + optional chevron menu (see LoadView/Cloning/TaskCard.tsx).
export function SplitButton({
  primaryLabel,
  primaryAriaLabel,
  primaryBusy,
  primaryTestId,
  primaryTooltip,
  segmentStyle,
  menuContentStyle,
  primaryButtonNamePosthog,
  onPrimaryClick,
  menuItems,
  menuTriggerAriaLabel,
  variant = 'default',
  size = 'default',
  segmentClassName,
  className,
  primaryClassName,
  menuTriggerClassName,
  disabled = false,
  logProperties,
}: SplitButtonProps) {
  const posthog = usePostHog();
  const hasMenu = Boolean(menuItems && menuItems.length > 0);

  const primaryButton = (
    <Button
      buttonNamePosthog={primaryButtonNamePosthog}
      logProperties={logProperties}
      variant={variant}
      size={size}
      type='button'
      aria-label={primaryAriaLabel}
      aria-busy={primaryBusy || undefined}
      data-testid={primaryTestId}
      style={segmentStyle}
      disabled={disabled}
      className={cn(
        'w-full min-w-0',
        hasMenu && 'rounded-r-none',
        segmentClassName,
        primaryClassName
      )}
      onClick={onPrimaryClick}
    >
      {primaryLabel}
    </Button>
  );

  if (!hasMenu || !menuItems) {
    return <div className={cn('w-full', className)}>{primaryButton}</div>;
  }

  return (
    <div
      className={cn(
        'isolate grid w-full min-w-0 grid-cols-[minmax(0,1fr)_2.25rem] items-center',
        className
      )}
    >
      <SplitButtonTooltip text={primaryTooltip}>
        {primaryButton}
      </SplitButtonTooltip>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            buttonNamePosthog={null}
            variant={variant}
            size={size}
            type='button'
            disabled={disabled}
            className={cn(
              'w-9 shrink-0 rounded-l-none px-0',
              segmentClassName,
              // Same brand fill as primary; darker seam/hover so the chevron
              // still reads as its own control without a taller secondary border.
              variant === 'default' &&
                'border-l border-brand-600 hover:bg-brand-600 data-[state=open]:bg-brand-600',
              menuTriggerClassName
            )}
            style={segmentStyle}
            aria-label={menuTriggerAriaLabel}
          >
            <ChevronDown size={15} aria-hidden='true' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align='end'
          className='w-64 p-0.5'
          style={menuContentStyle}
        >
          {menuItems.map((item) => {
            const isCompact = !item.description && !item.icon;

            return (
              <DropdownMenuItem
                key={item.id}
                aria-label={item.ariaLabel}
                className={
                  isCompact
                    ? 'h-8 min-h-8 cursor-pointer items-center px-3 py-0'
                    : 'cursor-pointer items-start gap-2 p-2'
                }
                onSelect={() => {
                  if (item.buttonNamePosthog) {
                    posthog?.capture(item.buttonNamePosthog, {
                      ...logProperties,
                    });
                  }
                  item.onSelect();
                }}
              >
                {item.icon}
                {isCompact ? (
                  renderMenuItemLabel(item.label, 'button', 'span')
                ) : (
                  <div className='min-w-0 flex-1'>
                    {renderMenuItemLabel(item.label, 'body-sm', 'div')}
                    {renderMenuItemDescription(item.description)}
                  </div>
                )}
                {item.trailing}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
